import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { supabase, describeError, type Row } from '@/lib/supabase'
import { pipelineKeys } from '@/features/pipeline/queries'

export type TeamMember = Row<'team_members'>
export type JobSeat = Row<'job_team'>

export const teamKeys = {
  members: ['team-members'] as const,
  forJob: (jobId: string) => ['job-team', jobId] as const,
}

export function useTeamMembers(includeInactive = false) {
  return useQuery({
    queryKey: [...teamKeys.members, includeInactive],
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<TeamMember[]> => {
      let query = supabase.from('team_members').select('*').order('sort_order').order('name')
      if (!includeInactive) query = query.eq('active', true)
      const { data, error } = await query
      if (error) throw error
      return data
    },
  })
}

export function useSaveTeamMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: Partial<TeamMember> & { name: string }) => {
      const { id, ...rest } = input
      const { error } = id
        ? await supabase.from('team_members').update(rest).eq('id', id)
        : await supabase.from('team_members').insert(rest)
      if (error) throw error
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: teamKeys.members }),
    onError: (error) => toast.error('Could not save', { description: describeError(error) }),
  })
}

export function useJobTeam(jobId: string) {
  return useQuery({
    queryKey: teamKeys.forJob(jobId),
    queryFn: async (): Promise<JobSeat[]> => {
      const { data, error } = await supabase.from('job_team').select('*').eq('job_id', jobId).order('position').order('created_at')
      if (error) throw error
      return data
    },
  })
}

export function useSaveSeat(jobId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { id?: string; role: JobSeat['role']; member_id: string | null; position?: number; deliverables?: string[]; fee?: number | null }) => {
      const { id, ...rest } = input
      const { error } = id
        ? await supabase.from('job_team').update(rest).eq('id', id)
        : await supabase.from('job_team').insert({ ...rest, job_id: jobId })
      if (error) throw error
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: teamKeys.forJob(jobId) }),
    onError: (error) => toast.error('Could not save the team', { description: describeError(error) }),
  })
}

/** Any change to a seat's booking: fee, deliverables, sending, signing state, release. */
export function useUpdateSeat(jobId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...patch }: Partial<JobSeat> & { id: string }) => {
      const { error } = await supabase.from('job_team').update(patch).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: teamKeys.forJob(jobId) }),
    onError: (error) => toast.error('Could not save the booking', { description: describeError(error) }),
  })
}

export function useRemoveSeat(jobId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('job_team').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: teamKeys.forJob(jobId) }),
    onError: (error) => toast.error('Could not remove that seat', { description: describeError(error) }),
  })
}

export function useConfirmTeam(jobId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (confirmed: boolean) => {
      const { error } = await supabase
        .from('jobs')
        .update({ team_confirmed_at: confirmed ? new Date().toISOString() : null })
        .eq('id', jobId)
      if (error) throw error
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.job(jobId) })
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.board })
    },
    onError: (error) => toast.error('Could not save', { description: describeError(error) }),
  })
}

// ----------------------------------------------------------------------------
// The crew database: one person's weddings, invoices and payments
// ----------------------------------------------------------------------------

export type CrewInvoice = Row<'crew_invoices'>
export type CrewPayment = Row<'crew_payments'>

type ContactName = { first_name: string; last_name: string | null }

/** A seat with the wedding it belongs to, for the person's record. */
export type CrewSeat = JobSeat & {
  job: {
    id: string
    kind: Row<'jobs'>['kind']
    title: string | null
    event_date: string
    venue_name: string | null
    primary_contact: ContactName | null
    partner_contact: ContactName | null
  } | null
}

const SEAT_WITH_JOB = `*, job:jobs!job_team_job_id_fkey ( id, kind, title, event_date, venue_name,
  primary_contact:contacts!jobs_primary_contact_id_fkey ( first_name, last_name ),
  partner_contact:contacts!jobs_partner_contact_id_fkey ( first_name, last_name ) )` as const

export const crewKeys = {
  seats: ['crew-seats'] as const,
  invoices: ['crew-invoices'] as const,
  payments: ['crew-payments'] as const,
  history: ['crew-history'] as const,
}

/** Every seat anyone has filled, with its wedding: the whole crew's history. */
export function useCrewSeats() {
  return useQuery({
    queryKey: crewKeys.seats,
    queryFn: async (): Promise<CrewSeat[]> => {
      const { data, error } = await supabase.from('job_team').select(SEAT_WITH_JOB).not('member_id', 'is', null)
      if (error) throw error
      return data as unknown as CrewSeat[]
    },
  })
}

export function useCrewPayments() {
  return useQuery({
    queryKey: crewKeys.payments,
    queryFn: async (): Promise<CrewPayment[]> => {
      const { data, error } = await supabase.from('crew_payments').select('*').order('paid_on', { ascending: false })
      if (error) throw error
      return data
    },
  })
}

export function useCrewInvoices() {
  return useQuery({
    queryKey: crewKeys.invoices,
    queryFn: async (): Promise<CrewInvoice[]> => {
      const { data, error } = await supabase.from('crew_invoices').select('*').order('invoice_date', { ascending: false })
      if (error) throw error
      return data
    },
  })
}

function useInvalidateCrew() {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: crewKeys.seats })
    void queryClient.invalidateQueries({ queryKey: crewKeys.payments })
    void queryClient.invalidateQueries({ queryKey: crewKeys.invoices })
    void queryClient.invalidateQueries({ queryKey: crewKeys.history })
    void queryClient.invalidateQueries({ queryKey: ['job-team'] })
  }
}

// ---------------------------------------------------------------------------
// Weddings added by hand: a Studio wedding they were not yet on, or one from
// before the Studio (crew_history).
// ---------------------------------------------------------------------------

export type CrewHistory = Row<'crew_history'>

export function useCrewHistory() {
  return useQuery({
    queryKey: crewKeys.history,
    queryFn: async (): Promise<CrewHistory[]> => {
      const { data, error } = await supabase.from('crew_history').select('*').order('event_date', { ascending: false })
      if (error) throw error
      return data
    },
  })
}

export function useAddPastWedding() {
  const invalidate = useInvalidateCrew()
  return useMutation({
    mutationFn: async (
      input:
        | { type: 'studio'; job_id: string; member_id: string; role: JobSeat['role']; fee: number | null }
        | { type: 'history'; row: Omit<CrewHistory, 'id' | 'created_at'> },
    ) => {
      const { error } =
        input.type === 'studio'
          ? await supabase.from('job_team').insert({ job_id: input.job_id, member_id: input.member_id, role: input.role, fee: input.fee })
          : await supabase.from('crew_history').insert(input.row)
      if (error) throw error
    },
    onSuccess: invalidate,
    onError: (error) => toast.error('Could not add the wedding', { description: describeError(error) }),
  })
}

export function useUpdatePastWedding() {
  const invalidate = useInvalidateCrew()
  return useMutation({
    mutationFn: async ({ id, ...patch }: Partial<CrewHistory> & { id: string }) => {
      const { error } = await supabase.from('crew_history').update(patch).eq('id', id)
      if (error) throw error
    },
    onSuccess: invalidate,
    onError: (error) => toast.error('Could not save that', { description: describeError(error) }),
  })
}

export function useRemovePastWedding() {
  const invalidate = useInvalidateCrew()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('crew_history').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: invalidate,
    onError: (error) => toast.error('Could not remove that', { description: describeError(error) }),
  })
}

/** A rating or note on one wedding. Allowed after signing: it isn't part of the agreement. */
export function useRateSeat() {
  const invalidate = useInvalidateCrew()
  return useMutation({
    mutationFn: async ({ id, ...patch }: { id: string; rating?: number | null; rating_note?: string | null }) => {
      const { error } = await supabase.from('job_team').update(patch).eq('id', id)
      if (error) throw error
    },
    onSuccess: invalidate,
    onError: (error) => toast.error('Could not save the rating', { description: describeError(error) }),
  })
}

export function useRecordPayment() {
  const invalidate = useInvalidateCrew()
  return useMutation({
    mutationFn: async (input: {
      member_id: string
      amount: number
      paid_on: string
      job_team_id?: string | null
      invoice_id?: string | null
      method?: 'eft' | 'cash' | 'other'
      reference?: string | null
    }) => {
      const { error } = await supabase.from('crew_payments').insert(input)
      if (error) throw error
    },
    onSuccess: () => {
      invalidate()
      toast.success('Payment recorded')
    },
    onError: (error) => toast.error('Could not record the payment', { description: describeError(error) }),
  })
}

export function useDeletePayment() {
  const invalidate = useInvalidateCrew()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('crew_payments').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: invalidate,
    onError: (error) => toast.error('Could not remove the payment', { description: describeError(error) }),
  })
}

const EXTENSION: Record<string, string> = {
  'application/pdf': 'pdf',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
}
export const INVOICE_FILE_TYPES = Object.keys(EXTENSION)

/** Saves an invoice they sent, uploading the file to the private crew bucket first. */
export function useSaveInvoice() {
  const invalidate = useInvalidateCrew()
  return useMutation({
    mutationFn: async (input: {
      member_id: string
      amount: number
      invoice_date: string
      invoice_number: string | null
      job_team_id: string | null
      file: File | null
    }) => {
      const { file, ...row } = input
      const id = crypto.randomUUID()
      let file_path: string | null = null
      if (file) {
        const ext = EXTENSION[file.type]
        if (!ext) throw new Error('Upload a PDF or a photo (JPEG, PNG or WebP).')
        file_path = `${input.member_id}/${id}.${ext}`
        const { error } = await supabase.storage.from('crew').upload(file_path, file, { contentType: file.type })
        if (error) throw error
      }
      const { error } = await supabase.from('crew_invoices').insert({ ...row, id, file_path })
      if (error) {
        if (file_path) await supabase.storage.from('crew').remove([file_path])
        throw error
      }
    },
    onSuccess: () => {
      invalidate()
      toast.success('Invoice saved')
    },
    onError: (error) => toast.error('Could not save the invoice', { description: describeError(error) }),
  })
}

export function useDeleteInvoice() {
  const invalidate = useInvalidateCrew()
  return useMutation({
    mutationFn: async (invoice: CrewInvoice) => {
      const { error } = await supabase.from('crew_invoices').delete().eq('id', invoice.id)
      if (error) throw error
      if (invoice.file_path) await supabase.storage.from('crew').remove([invoice.file_path])
    },
    onSuccess: invalidate,
    onError: (error) => toast.error('Could not remove the invoice', { description: describeError(error) }),
  })
}

/** Opens an invoice file through a link that works for five minutes. */
export async function openInvoiceFile(path: string) {
  const tab = window.open('', '_blank')
  const { data, error } = await supabase.storage.from('crew').createSignedUrl(path, 300)
  if (error || !data) {
    tab?.close()
    toast.error('Could not open the invoice', { description: describeError(error) })
    return
  }
  if (tab) tab.location.href = data.signedUrl
  else window.location.href = data.signedUrl
}
